import {FetchContext} from "../context/FetchContext";
import {useContext} from "react";
import {
    RefreshTokenAndRetry,
    uploadFileWithProgress,
    TypeOfTrack,
} from "../utils/utils";
import {AuthContext} from "../context/AuthContext";
import {AxiosRequestConfig} from "axios";
import {
    LocalAssetStore,
    ensureAudioRenditionProduced,
    ensureEnhancementRenditionProduced,
    markAudioRenditionInitialState,
    markEnhancementRenditionInitialState,
    ProcessAndUploadTrackNow,
} from "./LocalAssetStore";
import {ProjectAssetsStore} from "../state/ProjectAssetsStore";
import {
    DEFAULT_LIMITS,
    ExtractImageFileMetadata,
    ExtractMediaFileMetadata, shouldExtractEnhancementAudioRendition, shouldExtractOpusAudioRendition,
} from "./Media";
import {
    ComputeAudioPeaksWithLufs,
    EncodePeaksToDat,
    DecodePeaksDatToFloat32,
} from "./Waveform";
import { WaveformProgressStore } from "../state/WaveformProgressStore";
import { FocusStore } from "../state/FocusStore";
import {SetTrackHasWaveform, SetTrackLufsAnalysis} from "../offlineFriendly/Mutators";
import {collection, doc, setDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp, getDoc, getDocs, writeBatch} from "firebase/firestore";
import {db} from "../Firebase";
import { trackSessionEvent } from "../analytics/sessionEvents";
import {
    kAssetsCollectionName,
    kCompositionsCollectionName,
    kCompositionLayersSubcollectionName,
    kCompositionLayerClipsSubcollectionName,
    kProjectsCollectionName, kSequencesCollectionName, kSpeakersCollectionName,
    kTracksCollectionName, kFoldersCollectionName
} from "../utils/CollectionConstants";
import { ALL_FORMATS, BlobSource, CanvasSink, Input } from "mediabunny";

// Helper: get current time as Unix seconds (int64-compatible for Go server)
const nowUnixSec = () => Math.floor(Date.now() / 1000);
const PERSISTED_SCHEMA_VERSION = 1;

type QuickFocusFrame = { tSec: number; imageBase64: string };

const sampleQuickFocusFramesFromVideoFile = async (file: File, opts?: {
    analyzedUntilSec?: number;
    maxFrames?: number;
    targetWidthPx?: number;
    jpegQuality?: number;
}): Promise<QuickFocusFrame[]> => {
    const analyzedUntilSec = Math.max(0, Number(opts?.analyzedUntilSec ?? 60) || 60);
    const maxFrames = Math.max(1, Math.floor(Number(opts?.maxFrames ?? 20) || 20));
    const targetWidthPx = Math.max(120, Math.floor(Number(opts?.targetWidthPx ?? 256) || 256));
    const jpegQuality = Math.max(0.3, Math.min(0.9, Number(opts?.jpegQuality ?? 0.6) || 0.6));

    try {
        const source = new BlobSource(file);
        const input = new Input({ source, formats: ALL_FORMATS });
        const vTrack = await input.getPrimaryVideoTrack();
        if (!vTrack || !(await vTrack.canDecode())) return [];

        const duration = Math.max(0, Number(await vTrack.computeDuration()) || 0);
        const until = duration > 0 ? Math.min(analyzedUntilSec, duration) : analyzedUntilSec;
        if (!(until > 0)) return [];

        const times: number[] = [];
        const stepSec = 3;
        let t = 0;
        while (t < until - 1e-3 && times.length < maxFrames) {
            // Avoid exactly 0s which can be blank in some decoders.
            const tt = Math.max(0.05, t);
            times.push(tt);
            t += stepSec;
        }
        if (times.length === 0) times.push(Math.min(0.05, until));

        const displayW = Number((vTrack as any).displayWidth ?? vTrack.codedWidth ?? 0) || 0;
        const displayH = Number((vTrack as any).displayHeight ?? vTrack.codedHeight ?? 0) || 0;
        const sourceW = Math.max(1, displayW || 1);
        const sourceH = Math.max(1, displayH || 1);
        const targetH = Math.max(1, Math.round((targetWidthPx * sourceH) / sourceW));

        // Use MediaBunny CanvasSink for fast decode (no <video> element).
        const sink = new CanvasSink(vTrack, {
            width: targetWidthPx,
            height: targetH,
            fit: "fill",
            poolSize: 1,
            alpha: false,
        } as any);

        const outCanvas = document.createElement("canvas");
        outCanvas.width = targetWidthPx;
        outCanvas.height = targetH;
        const ctx = outCanvas.getContext("2d", { willReadFrequently: false });
        if (!ctx) return [];

        const frames: QuickFocusFrame[] = [];
        let idx = 0;

        // @ts-ignore - for-await supported in TS targeting ES2018+
        for await (const wrapped of sink.canvasesAtTimestamps(times)) {
            const tSec = times[idx];
            idx++;

            try {
                const srcCanvas = (wrapped as any)?.canvas;
                if (!srcCanvas) continue;
                ctx.drawImage(srcCanvas as any, 0, 0, outCanvas.width, outCanvas.height);
                const dataUrl = outCanvas.toDataURL("image/jpeg", jpegQuality);
                const base64 = String(dataUrl).split(",")[1] ?? "";
                if (base64) {
                    frames.push({ tSec: Number((tSec ?? 0).toFixed(3)), imageBase64: base64 });
                }
            } catch {
                // skip
            } finally {
                try { (wrapped as any)?.close?.(); } catch {}
            }

            if (idx >= times.length) break;
        }

        return frames;
    } catch {
        return [];
    }
};

export const useAssetStore = () => {

    const fetchContext = useContext(FetchContext);

    const authContext = useContext(AuthContext);

    // Helper: parse server waveform payload and persist locally
    const parseAndPersistWaveform = async (payload: any, trackId: string, denoised: boolean = false): Promise<ArrayBuffer | null> => {
        try {
            const url: string | undefined = payload?.url;
            if (!url) return null;
            // Fetch the signed URL and read as ArrayBuffer
            const resp = await fetch(url);
            if (!resp.ok) return null;
            const buf = await resp.arrayBuffer();
            try {
                await LocalAssetStore.SaveAudioWaveformData({
                    trackId,
                    projectId: ProjectAssetsStore.projectId as string,
                    datBuf: buf,
                    denoised,
                });
                return buf;
            } catch { /* ignore local save errors */
            }
        } catch {
            // swallow and return null to allow fallback
        }
        return null;
    };

    // Helper: fetch a signed thumbnail URL and persist to OPFS (best-effort)
    const cacheSignedThumbnail = async (thumbnailId: string, projectId: string, url: string): Promise<void> => {
        try {
            const resp = await fetch(url);
            if (!resp.ok) {
                console.log("Fetching signed thumbnail URL failed with status: ", resp.status);
                return;
            } else {
                console.log("Fetched signed thumbnail URL successfully");
            }
            const blob = await resp.blob();
            const file = new File([blob], `${thumbnailId}`, { type: blob.type || 'image/jpeg' });
            if (projectId) {
                console.log("Calling save thumbnail locally");
                await LocalAssetStore.SaveThumbnail({ thumbnailId, projectId, file });
                console.log("Saved thumbnail locally");
            }
        } catch (e) {
            console.warn("Failed to cache thumbnail locally:", e);
        }
    };

    const SetActiveTranscriptionIdForTrack = async ({
        trackId,
        userId,
        activeTranscriptionId,
    }: {
        trackId: string;
        userId: string;
        activeTranscriptionId: string;
    }): Promise<boolean> => {
        const safeTrackId = String(trackId || "").trim();
        const safeUserId = String(userId || "").trim();
        const safeActiveTranscriptionId = String(activeTranscriptionId || "").trim();
        if (!safeTrackId || !safeUserId || !safeActiveTranscriptionId) {
            return false;
        }

        const trackRef = doc(db, kTracksCollectionName, safeTrackId);
        const patch = {
            transcriptionState: "completed",
            activeTranscriptId: safeActiveTranscriptionId,
        };

        try {
            for (let attempt = 1; attempt <= 3; attempt++) {
                try {
                    await updateDoc(trackRef, patch);
                    return true;
                } catch (error: any) {
                    const code = String(error?.code || "");
                    const message = String(error?.message || "");
                    const isNotFound = code === "not-found" || message.includes("No document to update");
                    if (!isNotFound || attempt === 3) {
                        throw error;
                    }
                    await new Promise((resolve) => setTimeout(resolve, 150 * attempt));
                }
            }
        } catch (error) {
            console.warn("[SetActiveTranscriptionIdForTrack] Failed to update track:", safeTrackId, error);
        }

        return false;
    };

    const resolveUploadLanguageCode = (args: {
        requestedLanguageCode: any;
        projectId: any;
    }): string => {
        const requested = String(args.requestedLanguageCode ?? "").trim();
        if (requested && requested.toLowerCase() !== "auto") {
            return requested;
        }

        const targetProjectId = String(args.projectId ?? "").trim();
        const currentProjectId = String(ProjectAssetsStore.projectId ?? "").trim();
        if (targetProjectId && currentProjectId && targetProjectId === currentProjectId) {
            const projectDefault = String(ProjectAssetsStore.projectDefaultTranscriptionLanguage ?? "").trim();
            if (projectDefault && projectDefault.toLowerCase() !== "auto") {
                return projectDefault;
            }
        }

        return requested || "auto";
    };

    const UploadAsset = async ({
        projectId,
        projectType,
        inputRef,
        file,
        filetype,
        languageCode,
        assetTitle = "",
        isRecording = false,
        isScreenRecording = false,
        queueEnhancementOnUpload = false,
        skipTranscription = false,
        presetTranscriptId = "",
        transcriptionDisabled = false,
        activeTranscriptionId = "",
        onUploadInitiated,
    }: {
        projectId: any;
        projectType: any;
        inputRef?: any;
        file?: File;
        filetype: any;
        languageCode: any;
        assetTitle?: string;
        isRecording?: boolean;
        isScreenRecording?: boolean;
        queueEnhancementOnUpload?: boolean;
        skipTranscription?: boolean;
        presetTranscriptId?: string;
        transcriptionDisabled?: boolean;
        activeTranscriptionId?: string;
        onUploadInitiated?: (args: { assetId: string; trackId: string; projectId: string }) => void;
    }) => {

        let response;
        const safePresetTranscriptId = String(presetTranscriptId || "").trim();
        const safeActiveTranscriptionId = String(activeTranscriptionId || "").trim();
        const effectiveLanguageCode = resolveUploadLanguageCode({
            requestedLanguageCode: languageCode,
            projectId,
        });
        const shouldDisableTranscription = Boolean(transcriptionDisabled);
        if (shouldDisableTranscription && !safeActiveTranscriptionId) {
            throw new Error("activeTranscriptionId is required when transcriptionDisabled is true");
        }
        const shouldUsePresetTranscription = shouldDisableTranscription && Boolean(safeActiveTranscriptionId);
        const shouldSkipTranscription = Boolean(skipTranscription) || shouldDisableTranscription;

        const config: AxiosRequestConfig = {
            headers: { Authorization: `Bearer ${authContext.getToken()}` },
        };

        const selectedFile: File | undefined = file ?? (inputRef?.files?.[0] as File | undefined);
        if (!selectedFile) {
            throw new Error("No file selected for upload");
        }
        // Use || instead of ?? so empty string falls back to file name
        const title = assetTitle || selectedFile.name;

        try {
            response = await fetchContext.authAxios.post('/assets/upload', {
                projectId: projectId,
                projectType: projectType,
                isRecording: isRecording,
                isScreenRecording: Boolean(isScreenRecording),
                title: title,
                fileType: filetype,
                languageCode: effectiveLanguageCode,
                queueEnhancementOnUpload: Boolean(queueEnhancementOnUpload),
                ...(shouldSkipTranscription ? {skipTranscription: true} : {}),
                ...(safePresetTranscriptId ? {presetTranscriptId: safePresetTranscriptId} : {}),
            }, config);

            console.log("Initiated upload by creating rows on server side: ");
        } catch (error) {
            if (error.response.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
        }

        const userId = authContext.getUserId();
        const responseData = response?.data || {};
        const assetId = responseData.assetId;
        const trackId = responseData.trackId;
        const projectIdFromServer = responseData.projectId;
        const thumbnailUploadSignedUrl = responseData.thumbnailUploadSignedUrl;
        const waveformUploadSignedUrl = responseData.waveformUploadSignedUrl;

        // Prefer upload-init signed URLs from /assets/upload for the first immediate attempt.
        // All subsequent attempts still use /initiate/:trackId as fallback/resume.
        let initialTrackInitSignedUrl = String(responseData.signedUrl || "").trim();
        let initialAudioRenditionInitSignedUrl = String(responseData.audioRenditionUploadSignedUrl || "").trim();
        let initialEnhancementRenditionInitSignedUrl = String(responseData.enhancementRenditionUploadSignedUrl || "").trim();

        try {
            onUploadInitiated?.({
                assetId: String(assetId ?? ""),
                trackId: String(trackId ?? ""),
                projectId: String(projectIdFromServer ?? ""),
            });
        } catch (e) {
            console.warn("[UploadAsset] onUploadInitiated callback failed", e);
        }

        // Optimistic UI: make the uploaded file appear immediately in the project sidebar,
        // even before metadata extraction and before Firestore reflects the new track.
        try {
            if (filetype === "audio" || filetype === "video") {
                const optimisticTrackType =
                    filetype === "video"
                        ? (isRecording ? TypeOfTrack.LocalVideoRecording : TypeOfTrack.UploadedVideo)
                        : (isRecording ? TypeOfTrack.LocalAudioRecording : TypeOfTrack.UploadedAudio);
                ProjectAssetsStore.upsertOptimisticTrack({
                    id: trackId,
                    trackId,
                    assetId,
                    projectId: projectIdFromServer,
                    userId,
                    title,
                    deleted: false,
                    status: "uploading",
                    enhancedTrackState: queueEnhancementOnUpload ? "queued" : "",
                    trackType: optimisticTrackType,
                });
            }
        } catch (e) {
            console.warn("[UploadAsset] Failed to add optimistic track to ProjectAssetsStore", e);
        }

        // Create the asset or track locally, extract metadata and thumbnail and send to the server.
        let metadata;
        if (filetype === "audio" || filetype === "video") {
            console.log("Creating track on local store")
            await LocalAssetStore.CreateTrack({trackId, userId, assetId, projectId: projectIdFromServer, title, file: selectedFile, createTime: Date.now()});
            console.log("Track created on local store. Preparing to extract metadata and thumbnail");
            metadata = await ExtractMediaFileMetadata({file: selectedFile, fileType: filetype});

            console.log("Metadata extracted. metadata: ", metadata);

            // ─────────────────────────────────────────────────────────────────────
            // Quick focus (client-origin chunk0 publish)
            // - Sample ~20 low-res frames from the first ~60s and send to the server:
            //   POST /focus/{trackId}/chunk0 -> publishes chunk0 + updates Firestore analysis doc.
            // - Also set an immediate heuristic summary in memory so default placement can use it instantly,
            //   even before the server publishes the chunk.
            // ─────────────────────────────────────────────────────────────────────
            try {
                if (filetype === "video") {
                    const totalSec = Math.max(0, Number(metadata?.duration ?? 0) || 0);
                    const analyzedUntilSec = Math.max(0, Math.min(60, totalSec));
                    const isScreen = Boolean(isScreenRecording);
                    const rect = isScreen
                        ? { x: 0, y: 0, w: 1, h: 1 }
                        : { x: 0.25, y: 0.05, w: 0.5, h: 0.6 };
                    const confidence = isScreen ? 1 : 0.15;

                    FocusStore.setQuickSummary({
                        trackId,
                        summary: {
                            rect,
                            confidence,
                            analyzedUntilSec,
                            provenance: "client",
                            updatedAtUnixSec: nowUnixSec(),
                        },
                    });

                    // Publish chunk0 synchronously so the client can consume via Firestore subscription
                    // without any polling. Best-effort: failures fall back to heuristic summary only.
                    try {
                        if (analyzedUntilSec > 0) {
                            const frames = await sampleQuickFocusFramesFromVideoFile(selectedFile, {
                                analyzedUntilSec,
                                maxFrames: 20,
                                targetWidthPx: 256,
                                jpegQuality: 0.6,
                            });
                            if (frames.length > 0) {
                                await PostFocusChunk0({ trackId, totalSec, frames });
                            }
                        }
                    } catch (e) {
                        console.warn("[UploadAsset] Failed to publish focus chunk0", e);
                    }
                }
            } catch {}

            let hasThumbnail = false;
            if (filetype === "video" && metadata.thumbnail) {
                try {
                    await LocalAssetStore.SaveThumbnail({thumbnailId: trackId, projectId: projectIdFromServer, file: metadata.thumbnail});
                    hasThumbnail = true;
                } catch (e) {
                    console.warn("Failed to save thumbnail locally: ", e);
                }
            } else if (filetype === "video" && !metadata.thumbnail) {
                console.warn("No thumbnail extracted from video file");
            }

            const shouldPrepareDerivedAudio = !shouldDisableTranscription && metadata.hasAudioStream && shouldExtractOpusAudioRendition({
                isVideo: metadata.hasVideoStream,
                container: metadata.container,
                sizeBytes: metadata.fileSize,
                durationSec: metadata.duration,
            }, DEFAULT_LIMITS);

            const shouldPrepareDerivedAudioForEnhancement = metadata.hasAudioStream && shouldExtractEnhancementAudioRendition({
                isVideo: metadata.hasVideoStream,
                container: metadata.container,
                sizeBytes: metadata.fileSize,
                durationSec: metadata.duration,
            });
            const enhancementRenditionState = !metadata.hasAudioStream
                ? "NOT_NEEDED"
                : shouldPrepareDerivedAudioForEnhancement
                    ? "CLIENT_EXTRACTING"
                    : metadata.hasVideoStream || metadata.container === "wav"
                        ? "PENDING"
                        : "NOT_NEEDED";

            await fetchContext.authAxios.post('/metadata', {
                width: metadata.width,
                height: metadata.height,
                duration: metadata.duration,
                sampleRate: metadata.sampleRate,
                videoBitrate: metadata.videoBitrate,
                audioBitrate: metadata.audioBitrate,
                hasAudioStream: metadata.hasAudioStream,
                hasThumbnail: hasThumbnail,
                fileSize: metadata.fileSize,
                assetId: assetId,
                trackId: trackId,
                projectId: projectIdFromServer,
                filetype: filetype,
                mimetype: metadata.mimeType,
                transcriptionState: !metadata.hasAudioStream ? "NOT_NEEDED" : shouldUsePresetTranscription ? "completed" : shouldPrepareDerivedAudio ? "CLIENT_EXTRACTING" : "PENDING",
                enhancementRenditionState,
            }, config);
            if (shouldUsePresetTranscription) {
                await SetActiveTranscriptionIdForTrack({
                    trackId: String(trackId || ""),
                    userId: String(userId || ""),
                    activeTranscriptionId: safeActiveTranscriptionId,
                });
            }
            if (hasThumbnail) {
                await uploadFileWithProgress(thumbnailUploadSignedUrl, metadata.thumbnail, (percentCompleted) => {
                    console.log("Thumbnail updated percent completed: ", percentCompleted)
                }, () => {
                    console.log("Thumbnail update done")
                });
            }
            await Promise.all([
                markAudioRenditionInitialState(trackId, shouldPrepareDerivedAudio),
                markEnhancementRenditionInitialState(trackId, shouldPrepareDerivedAudioForEnhancement),
            ]);

            // Start rendition extraction ASAP (non-blocking).
            if (shouldPrepareDerivedAudio) {
                void (async () => {
                    try {
                        await ensureAudioRenditionProduced(trackId);
                    } catch (e) {
                        console.warn("ensureAudioRenditionProduced failed:", e);
                    }
                })();
            }

            if (shouldPrepareDerivedAudioForEnhancement) {
                void (async () => {
                    try {
                        await ensureEnhancementRenditionProduced(trackId);
                    } catch (e) {
                        console.warn("ensureEnhancementRenditionProduced failed:", e);
                    }
                })();
            }



            // Compute waveform + LUFS in background (non-blocking)
            void (async () => {
            try {
                    const {basePeaks, meta, lufs} = await ComputeAudioPeaksWithLufs(selectedFile, {
                        onProgress: ({ basePeaks, durationSec, processedUntilSec }) => {
                            WaveformProgressStore.updateProgress({
                                trackId,
                                peaks: basePeaks,
                                durationSec,
                                processedUntilSec,
                            });
                        },
                        // Show something quickly, then fill in by time.
                        progressInitialSec: 15,
                        progressEverySec: 60,
                    });
                const datBuf = EncodePeaksToDat(basePeaks);

                    // Save to local storage
                    try {
                        await LocalAssetStore.SaveAudioWaveformData({
                            trackId,
                            projectId,
                            datBuf,
                            durationSec: metadata.duration,
                            buckets: basePeaks.length,
                            encoding: 'peak_u16_v1'
                        });
                } catch (error) {
                    console.log("Error saving audio peaks: ", error);
                }

                    // Mark waveform as ready
                await SetTrackHasWaveform({trackId});

                    // Store LUFS analysis results
                    await SetTrackLufsAnalysis({
                        trackId,
                        lufsState: 'ready',
                        integratedLufs: lufs.integratedLufs,
                        recommendedGain: lufs.recommendedGain,
                    });
                    console.log("Waveform + LUFS ready for track:", trackId, lufs);

                    // Upload waveform to server
                try {
                    await uploadFileWithProgress(waveformUploadSignedUrl, datBuf, (percentCompleted) => {
                        console.log("Waveform updated percent completed: ", percentCompleted)
                    }, () => {
                        console.log("Waveform update done")
                    })
                } catch (e) {
                    console.error("Error uploading waveform: ", e);
                }
            } catch (waveformError) {
                    console.error("Failed to compute waveform with LUFS:", waveformError);
            } finally {
                    WaveformProgressStore.clear(trackId);
            }
            })();

        } else { // Image upload [Editor image, or brand logo in studio]
            await LocalAssetStore.CreateAsset({assetId, userId, projectId: projectIdFromServer, title, file: selectedFile, createTime: Date.now()});
            metadata = await ExtractImageFileMetadata({file: selectedFile});
            await LocalAssetStore.SaveThumbnail({thumbnailId: trackId, projectId: projectIdFromServer, file: metadata.thumbnail});
            await fetchContext.authAxios.post('/metadata', {
                assetId: assetId,
                trackId: trackId,
                projectId: projectIdFromServer,
                filetype: filetype,
                mimetype: metadata.mimeType,
                width: metadata.width,
                height: metadata.height,
            }, config);
            await uploadFileWithProgress(thumbnailUploadSignedUrl, metadata.thumbnail, () => {}, () => {});
        }

        console.log("Starting background upload of file to GCS:")

        void (async () => {
            await ProcessAndUploadTrackNow(String(trackId || ""), async (trackIdForUpload, kind) => {
                console.log("Initiating resumable upload for current track: ", trackIdForUpload);
                const uploadKind = String(kind || "track");
                const isCurrentTrack = String(trackIdForUpload || "").trim() === String(responseData.trackId || "").trim();
                if (isCurrentTrack) {
                    let preferredInitSignedUrl = "";
                    if (uploadKind === "track") {
                        preferredInitSignedUrl = initialTrackInitSignedUrl;
                        initialTrackInitSignedUrl = "";
                    } else if (uploadKind === "audio-rendition") {
                        preferredInitSignedUrl = initialAudioRenditionInitSignedUrl;
                        initialAudioRenditionInitSignedUrl = "";
                    } else if (uploadKind === "enhancement-rendition") {
                        preferredInitSignedUrl = initialEnhancementRenditionInitSignedUrl;
                        initialEnhancementRenditionInitSignedUrl = "";
                    }
                    if (preferredInitSignedUrl) {
                        console.log(`[UploadAsset] Using initial signed URL from /assets/upload for kind=${uploadKind}`);
                        return preferredInitSignedUrl;
                    }
                    console.log(`[UploadAsset][init-url] Missing initial signed URL for current track kind=${uploadKind}; falling back to /initiate`);
                } else {
                    console.log(`[UploadAsset][init-url] Processing a different pending track=${trackIdForUpload} kind=${uploadKind}; using /initiate`);
                }

                try {
                    const config = {
                        headers: {Authorization: `Bearer ${authContext.getToken()}`},
                    };
                    const {data} = await fetchContext.authAxios.get(
                        `/initiate/${encodeURIComponent(trackIdForUpload)}?kind=${encodeURIComponent(uploadKind)}`,
                        config
                    );
                    // Expecting { url: string }
                    return data?.url;
                } catch (error) {
                    if (error?.response?.status === 401) {
                        await RefreshTokenAndRetry(error, authContext, fetchContext);
                    }
                    if (error?.response?.status === 404) {
                        // Let LocalAssetStore handle stale-track cleanup after one retry.
                        throw error;
                    }
                    return undefined;
                }
            }, userId);
        })();

        return assetId;

    }

    // Server-first upload path:
    // - Skip client-side preprocessing (metadata/thumbnail/waveform/focus)
    // - Save a temporary local copy solely for resumable upload reliability (OPFS/Dexie)
    // - Upload original bytes via existing resumable infra (/initiate?kind=server-first)
    // - Delete the temporary local copy once uploaded
    // - Asset becomes usable only once server processing flips status to "ready"
    const UploadNonLocalFirstAsset = async ({
        projectId,
        projectType,
        inputRef,
        file,
        filetype,
        languageCode,
        assetTitle,
        isRecording,
        isScreenRecording,
    }: {
        projectId: any;
        projectType: any;
        inputRef?: any;
        file?: File;
        filetype: any;
        languageCode: any;
        assetTitle?: string;
        isRecording?: boolean;
        isScreenRecording?: boolean;
    }) => {
        if (filetype !== "audio" && filetype !== "video") {
            throw new Error("UploadNonLocalFirstAsset only supports audio/video uploads");
        }

        let response: any;
        const effectiveLanguageCode = resolveUploadLanguageCode({
            requestedLanguageCode: languageCode,
            projectId,
        });
        const config: AxiosRequestConfig = {
            headers: { Authorization: `Bearer ${authContext.getToken()}` },
        };

        const selectedFile: File | undefined = file ?? (inputRef?.files?.[0] as File | undefined);
        if (!selectedFile) {
            throw new Error("No file selected for upload");
        }

        // Use || instead of ?? so empty string falls back to file name
        const title = assetTitle || selectedFile.name;

        try {
            response = await fetchContext.authAxios.post(
                "/assets/upload",
                {
                    projectId: projectId,
                    projectType: projectType,
                    isRecording: isRecording,
                    isScreenRecording: Boolean(isScreenRecording),
                    title: title,
                    fileType: filetype,
                    languageCode: effectiveLanguageCode,
                    serverSideFirst: true,
                },
                config,
            );
        } catch (error: any) {
            if (error?.response?.status === 401) {
                response = await RefreshTokenAndRetry(error, authContext, fetchContext);
            } else {
                throw error;
            }
        }

        const userId = authContext.getUserId();
        const responseData = response?.data || {};
        const assetId = responseData.assetId;
        const trackId = responseData.trackId;
        const projectIdFromServer = responseData.projectId;
        const initialServerFirstInitSignedUrl = String(responseData.signedUrl || "").trim();

        if (!assetId || !trackId) {
            throw new Error("Server did not return assetId/trackId");
        }

        // Optimistic UI: show the file immediately, even though it's not locally available.
        try {
            const optimisticTrackType =
                filetype === "video"
                    ? (isRecording ? TypeOfTrack.LocalVideoRecording : TypeOfTrack.UploadedVideo)
                    : (isRecording ? TypeOfTrack.LocalAudioRecording : TypeOfTrack.UploadedAudio);
            ProjectAssetsStore.upsertOptimisticTrack({
                id: trackId,
                trackId,
                assetId,
                projectId: projectIdFromServer || projectId,
                userId,
                title,
                deleted: false,
                status: "uploading",
                trackType: optimisticTrackType,
            });
        } catch (e) {
                console.warn("[UploadNonLocalFirstAsset] Failed to add optimistic track", e);
        }

        // Save a temporary local copy (upload-only) so we can use the existing resumable upload infra.
        // This local file is deleted once the resumable upload completes.
        try {
            await LocalAssetStore.CreateUploadOnlyTrack({
                trackId,
                userId,
                assetId,
                projectId: projectIdFromServer || projectId,
                title,
                file: selectedFile,
                createTime: Date.now(),
            });
        } catch (e) {
            console.error("[UploadNonLocalFirstAsset] Failed to save upload-only local copy:", e);
            throw e;
        }

        // Upload original bytes to server storage in the background via resumable upload.
        void (async () => {
            try {
                await ProcessAndUploadTrackNow(String(trackId || ""), async (trackIdForUpload, kind) => {
                    console.log("Initiating resumable upload for current track: ", trackIdForUpload, "kind:", kind);
                    const uploadKind = String(kind || "track");
                    const isCurrentTrack = String(trackIdForUpload || "").trim() === String(responseData.trackId || "").trim();
                    if (isCurrentTrack && uploadKind === "server-first" && initialServerFirstInitSignedUrl) {
                        console.log("[UploadNonLocalFirstAsset] Using initial signed URL from /assets/upload for server-first upload");
                        return initialServerFirstInitSignedUrl;
                    }
                    if (isCurrentTrack && uploadKind === "server-first" && !initialServerFirstInitSignedUrl) {
                        console.log("[UploadNonLocalFirstAsset][init-url] Missing initial server-first URL for current track; falling back to /initiate");
                    }
                    try {
                        const config = {
                            headers: {Authorization: `Bearer ${authContext.getToken()}`},
                        };
                        const {data} = await fetchContext.authAxios.get(
                            `/initiate/${encodeURIComponent(trackIdForUpload)}?kind=${encodeURIComponent(uploadKind)}`,
                            config
                        );
                        // Expecting { url: string }
                        return data?.url;
                    } catch (error) {
                        if (error?.response?.status === 401) {
                            await RefreshTokenAndRetry(error, authContext, fetchContext);
                        }
                        if (error?.response?.status === 404) {
                            // Let LocalAssetStore handle stale-track cleanup after one retry.
                            throw error;
                        }
                        return undefined;
                    }
                }, userId);
            } catch (e) {
                console.error("[UploadNonLocalFirstAsset] Background upload failed:", e);
            }
        })();

        return assetId;
    };

    const GetAssetUrl = async ({assetId}) : Promise<string | undefined> => {
        if (!assetId) return undefined;

        try {
            const localUrl = await LocalAssetStore.GetAssetSource({ assetId });
            console.log("Asset found locally: ", localUrl);
            if (localUrl) return localUrl;
        } catch (_) {}
    }

    const RenameTrack = async ({newName, trackId}) => {
        try {
            const renameValue = (newName || "").trim();
            if (!renameValue) return;
            const entityRef = doc(db, kTracksCollectionName, trackId);
            await updateDoc(entityRef, { title: renameValue });
        } catch (e) {
            console.error("Failed to rename track", e);
        }
    };

    const RenameAsset = async ({newName, assetId}) => {
        try {
            const renameValue = (newName || "").trim();
            if (!renameValue) return;
            const entityRef = doc(db, kAssetsCollectionName, assetId);
            await updateDoc(entityRef, { title: renameValue });
        } catch (e) {
            console.error("Failed to rename asset", e);
        }
    }

    const RenameTrackOrAsset = async ({newName, entityId, isTrack}) => {
        if (isTrack) {
            await RenameTrack({newName, trackId: entityId});
        } else {
            await RenameAsset({newName, assetId: entityId});
        }
    }

    const RenameComposition = async ({newName, compositionId}) => {
        try {
            const renameValue = (newName || "").trim();
            if (!renameValue) return;
            const entityRef = doc(db, kCompositionsCollectionName, compositionId);
            await updateDoc(entityRef, { title: renameValue });
        } catch (e) {
            console.error("Failed to rename composition", e);
        }
    }

    const DuplicateComposition = async ({
        compositionId,
        titleOverride,
    }: {
        compositionId: string;
        titleOverride?: string;
    }): Promise<string> => {
        const DEBUG = process.env.NODE_ENV !== "production";
        const log = (...args: any[]) => {
            if (!DEBUG) return;
            console.log("[DuplicateComposition]", ...args);
        };
        const logError = (...args: any[]) => {
            if (!DEBUG) return;
            console.error("[DuplicateComposition]", ...args);
        };

        if (!compositionId) {
            throw new Error("Missing compositionId");
        }

        log("start", { compositionId });

        const userId = authContext.getUserId?.() || authContext.authState?.userId || "";
        if (!userId || typeof userId !== "string") {
            throw new Error("Not authenticated");
        }
        const workspaceId =
            authContext.getCurrentWorkspaceId?.() ||
            authContext.getDefaultWorkspaceId?.() ||
            (typeof userId === "string" && userId ? userId : undefined);

        log("auth", { userId, workspaceId });

        const sourceCompRef = doc(db, kCompositionsCollectionName, compositionId);
        const sourceSnap = await getDoc(sourceCompRef);
        if (!sourceSnap.exists()) {
            throw new Error("Source composition not found");
        }

        const sourceData: any = sourceSnap.data() || {};
        const sourceTitle = String(sourceData.title ?? sourceData.name ?? "Untitled").trim() || "Untitled";
        const overrideTitle = typeof titleOverride === "string" ? titleOverride.trim() : "";
        const newTitle = overrideTitle || `Copy of ${sourceTitle}`;

        log("loaded source", {
            sourceTitle,
            projectId: sourceData.projectId,
            schemaVersion: sourceData.schemaVersion,
            isEmpty: sourceData.isEmpty,
        });

        // Read layers + clips first so we don't create partial duplicates if a read fails midway.
        // IMPORTANT: Prefer orderedLayerIds/orderedClipIds to avoid Firestore rules that may disallow list().
        const orderedLayerIds: string[] = Array.isArray(sourceData.orderedLayerIds)
            ? sourceData.orderedLayerIds.filter((id: any) => typeof id === "string" && id.length > 0)
            : [];

        log("loading layers+clips", { orderedLayerIds: orderedLayerIds.length });

        const layers: Array<{
            layerId: string;
            data: any;
            clips: Array<{ clipId: string; data: any }>;
        }> = [];

        let totalSourceClips = 0;

        if (orderedLayerIds.length > 0) {
            for (const layerId of orderedLayerIds) {
                const layerDocRef = doc(collection(sourceCompRef, kCompositionLayersSubcollectionName), layerId);
                let layerSnap;
                try {
                    layerSnap = await getDoc(layerDocRef);
                } catch (e) {
                    logError("failed to read layer doc", { compositionId, layerId, path: layerDocRef.path }, e);
                    throw e;
                }
                if (!layerSnap.exists()) continue;
                const layerData = layerSnap.data() || {};

                const clipsCol = collection(layerDocRef, kCompositionLayerClipsSubcollectionName);
                const orderedClipIds: string[] = Array.isArray(layerData.orderedClipIds)
                    ? layerData.orderedClipIds.filter((id: any) => typeof id === "string" && id.length > 0)
                    : [];

                const clips: Array<{ clipId: string; data: any }> = [];
                if (orderedClipIds.length > 0) {
                    const seen = new Set<string>();
                    for (const clipId of orderedClipIds) {
                        if (seen.has(clipId)) continue;
                        seen.add(clipId);
                        const clipDocRef = doc(clipsCol, clipId);
                        let clipSnap;
                        try {
                            clipSnap = await getDoc(clipDocRef);
                        } catch (e) {
                            logError(
                                "failed to read clip doc",
                                { compositionId, layerId, clipId, path: clipDocRef.path },
                                e
                            );
                            throw e;
                        }
                        if (!clipSnap.exists()) continue;
                        clips.push({ clipId, data: clipSnap.data() || {} });
                    }
                } else {
                    // Fallback: list clips if orderedClipIds isn't present (may be denied by rules).
                    try {
                        const clipSnaps = await getDocs(clipsCol);
                        for (const d of clipSnaps.docs) {
                            clips.push({ clipId: String(d.id), data: d.data() || {} });
                        }
                    } catch (e) {
                        logError(
                            "failed to list clips (likely disallowed by Firestore rules); orderedClipIds missing",
                            { compositionId, layerId, path: clipsCol.path },
                            e
                        );
                        throw e;
                    }
                }

                totalSourceClips += clips.length;
                layers.push({ layerId, data: layerData, clips });
            }
        } else {
            // Fallback: list layers if orderedLayerIds isn't present (may be denied by rules).
            const sourceLayersCol = collection(sourceCompRef, kCompositionLayersSubcollectionName);
            try {
                const layerSnaps = await getDocs(sourceLayersCol);
                for (const l of layerSnaps.docs) {
                    const layerId = String(l.id);
                    const layerData = l.data() || {};
                    const clipsCol = collection(l.ref, kCompositionLayerClipsSubcollectionName);
                    const clipSnaps = await getDocs(clipsCol);
                    const clips = clipSnaps.docs.map((d) => ({ clipId: String(d.id), data: d.data() || {} }));
                    totalSourceClips += clips.length;
                    layers.push({ layerId, data: layerData, clips });
                }
            } catch (e) {
                logError(
                    "failed to list layers (likely disallowed by Firestore rules); orderedLayerIds missing",
                    { compositionId, path: sourceLayersCol.path },
                    e
                );
                throw e;
            }
        }

        log("loaded layers+clips", { layers: layers.length, clips: totalSourceClips });

        const newCompRef = doc(collection(db, kCompositionsCollectionName)); // auto-id
        const newCompId = newCompRef.id;
        const now = nowUnixSec();

        const projectId =
            String(sourceData.projectId ?? "") ||
            String(ProjectAssetsStore.projectId ?? "") ||
            "";

        const schemaVersion = Number.isFinite(sourceData.schemaVersion)
            ? Number(sourceData.schemaVersion)
            : PERSISTED_SCHEMA_VERSION;

        const newCompData: any = {
            ...sourceData,
            compositionId: newCompId,
            title: newTitle,
            projectId,
            userId,
            ...(workspaceId ? { workspaceId } : {}),
            schemaVersion,
            deleted: false,
            // Ensure restore runs for duplicated compositions (even if they contain only empty layers).
            isEmpty: false,
            createdAt: now,
            updatedAt: now,
        };

        // Create the parent composition doc first.
        // (Many Firestore rules for layers/clips reference the parent doc via get()/exists(),
        // which won't see a parent created in the same batch.)
        log("creating parent composition doc", {
            newCompId,
            newTitle,
            projectId,
            schemaVersion,
            keys: Object.keys(newCompData || {}).length,
            fields: Object.keys(newCompData || {}).sort(),
        });
        try {
            await setDoc(newCompRef, newCompData);
            log("created parent composition doc", { newCompId });
        } catch (e) {
            logError("failed to create parent composition doc", { newCompId, projectId, workspaceId }, e);
            throw e;
        }

        try {
            const MAX_BATCH_WRITES = 450; // Firestore hard-limit is 500 ops per batch.
            let batch = writeBatch(db);
            let writes = 0;
            let totalWrites = 0;
            let commits = 0;

            const flush = async () => {
                if (writes === 0) return;
                const committingWrites = writes;
                await batch.commit();
                commits += 1;
                log("committed batch", { newCompId, commits, writes: committingWrites, totalWrites });
                batch = writeBatch(db);
                writes = 0;
            };

            const queueSet = async (ref: any, data: any) => {
                batch.set(ref, data);
                writes += 1;
                totalWrites += 1;
                if (writes >= MAX_BATCH_WRITES) {
                    await flush();
                }
            };

            log("copying layers+clips", { newCompId });
            for (const layer of layers) {
                const newLayerRef = doc(
                    collection(newCompRef, kCompositionLayersSubcollectionName),
                    layer.layerId
                );
                const newLayerData: any = {
                    ...layer.data,
                    id: layer.layerId,
                    compositionId: newCompId,
                    userId,
                    ...(workspaceId ? { workspaceId } : {}),
                    createdAt: now,
                    updatedAt: now,
                    schemaVersion,
                };
                await queueSet(newLayerRef, newLayerData);

                for (const clip of layer.clips) {
                    const newClipRef = doc(
                        collection(newLayerRef, kCompositionLayerClipsSubcollectionName),
                        clip.clipId
                    );
                    const newClipData: any = {
                        ...clip.data,
                        id: clip.clipId,
                        compositionId: newCompId,
                        layerId: layer.layerId,
                        userId,
                        ...(workspaceId ? { workspaceId } : {}),
                        createdAt: now,
                        updatedAt: now,
                        schemaVersion,
                    };
                    await queueSet(newClipRef, newClipData);
                }
            }

            await flush();
            log("copied layers+clips", { newCompId, commits, totalWrites });
            trackSessionEvent("composition_duplicated");
            return newCompId;
        } catch (e) {
            logError("failed to copy layers+clips; marking duplicate deleted", { newCompId }, e);
            // Best-effort hide the partially-created duplicate if layers/clips copy fails.
            try {
                await updateDoc(newCompRef, { deleted: true, updatedAt: nowUnixSec() });
                log("marked duplicate deleted", { newCompId });
            } catch {}
            throw e;
        }
    };

    const DeleteComposition = async ({compositionId}) => {
        try {
            const entityRef = doc(db, kCompositionsCollectionName, compositionId);
            await updateDoc(entityRef, {deleted: true});
        } catch (e) {
            console.error("Failed to rename composition", e);
        }
    }

    const RenameSequence = async ({newName, sequenceId}) => {
        try {
            const renameValue = (newName || "").trim();
            if (!renameValue) return;
            const entityRef = doc(db, kSequencesCollectionName, sequenceId);
            await updateDoc(entityRef, { title: renameValue });
        } catch (e) {
            console.error("Failed to rename sequence", e);
        }
    }

    const GetTrackUrl = async ({trackId}) : Promise<string | undefined> => {
        if (!trackId) return undefined;

        try {
            const localUrl = await LocalAssetStore.GetTrackSource({ trackId });
            if (localUrl) return localUrl;
        } catch (_) {}


        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${authContext.getToken()}` },
            };
            const { data } = await fetchContext.authAxios.get(
                `/track/${encodeURIComponent(trackId)}`,
                config
            );
            // Expecting { url: string }
            return data?.url as (string | undefined);
        } catch (error: any) {
            if (error?.response?.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
            return undefined;
        }
    }

    const ResolveAssetUrl = async (assetId: string, type: 'audio'|'video'|'image') => {
        let url: string;
        if (type === "image") {
            url = await GetAssetUrl({ assetId });
            return url;
        }
        url = await GetTrackUrl({ trackId: assetId });
        return url;
    };

    const GetThumbnailUrl = async ({thumbnailId, projectId}: { thumbnailId: string, projectId: string }): Promise<string | undefined> => {
        if (!thumbnailId) return undefined;

        // 1) Try local first
        try {
            const localUrl = await LocalAssetStore.GetThumbnail({ thumbnailId });
            if (localUrl)  {
                return localUrl;
            }
        } catch (_) { /* ignore and fallback to server */ }

        // 2) Fallback to server signed URL
        const storedToken = authContext.getToken?.();
        const token = storedToken || await authContext.getFreshToken?.();
        if (!token) {
            // Avoid making server requests with missing/undefined tokens during auth initialization.
            return undefined;
        }

        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${token}` },
            };
            const { data } = await fetchContext.authAxios.get(
                `/thumbnail/${encodeURIComponent(thumbnailId)}`,
                config
            );
            // Expecting { url: string }
            const url = data?.url as (string | undefined);
            if (url) void cacheSignedThumbnail(thumbnailId, projectId, url);
            return url;
        } catch (error: any) {
            if (error?.response?.status === 401) {
                const resp = await RefreshTokenAndRetry(error, authContext, fetchContext);
                const url = resp?.data?.url as (string | undefined);
                if (url) void cacheSignedThumbnail(thumbnailId, projectId, url);
                return url;
            }
            return undefined;
        }
    }

    const DeleteProject = async ({projectId}) => {
        try {
            const entityRef = doc(db, kProjectsCollectionName, projectId);
            await updateDoc(entityRef, { deleted: true });
        } catch (e) {
            console.error("Failed to delete project", e);
        }
    }

    const BringLegacyProjectToNewLibretto = async ({projectId}: { projectId: string }): Promise<any | undefined> => {
        if (!projectId) return undefined;

        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${authContext.getToken()}` },
            };

            const { data } = await fetchContext.authAxios.post(
                `/project/${encodeURIComponent(projectId)}/bring`,
                {},
                config
            );
            return data;
        } catch (error: any) {
            if (error?.response?.status === 401) {
                const resp = await RefreshTokenAndRetry(error, authContext, fetchContext);
                return resp?.data;
            }
            throw error;
        }
    }

    const UndeleteProject = async ({projectId}) => {
        try {
            const entityRef = doc(db, kProjectsCollectionName, projectId);
            await updateDoc(entityRef, { deleted: false });
        }  catch (e) {
            console.error("Failed to undelete project", e);
        }
    }

    const DeleteTrack = async ({trackId}) => {
        const config: AxiosRequestConfig = {
            headers: { Authorization: `Bearer ${authContext.getToken()}` },
        };

        console.log("Delete track call for trackId: ", trackId);

        await Promise.all([
            fetchContext.authAxios.post(`/tracks/delete`, {
                trackIds: [trackId],
            }, config).then(() => {
            }).catch((err) => {
                if (err.response.status === 401) {
                    RefreshTokenAndRetry(err, authContext, fetchContext);
                }
            }),

            // LocalAssetStore.DeleteTrack({trackId})
        ]);
    }

    const DeleteAsset = async ({assetId}) => {
        const config: AxiosRequestConfig = {
            headers: { Authorization: `Bearer ${authContext.getToken()}` },
        };

        await Promise.all([
            fetchContext.authAxios.delete(`/assets/${assetId}`, config).then(() => {

            }).catch((err) => {
                if (err.response.status === 401) {
                    RefreshTokenAndRetry(err, authContext, fetchContext);
                }
            }),

            // LocalAssetStore.DeleteAsset({assetId})
        ]);
    }

    const GetWaveformData = async ({trackId, denoised = false}: { trackId: string; denoised?: boolean }): Promise<Float32Array> => {
        // Check local IndexedDB storage first
        try {
            const local = await LocalAssetStore.GetAudioWaveformData({ trackId, denoised });
            if (local) {
                let peaksF32: Float32Array | null = null;
                if (local instanceof Float32Array) {
                    peaksF32 = local;
                } else if (local instanceof ArrayBuffer) {
                    peaksF32 = DecodePeaksDatToFloat32(local);
                }
                if (peaksF32 && peaksF32.length > 0) return peaksF32;
            }
        } catch {
        }
        // Server fallback: GET /waveform/:trackId or /waveform/:trackId/enhanced
        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${authContext.getToken()}` },
            };
            const endpoint = denoised
                ? `/waveform/${encodeURIComponent(trackId)}/enhanced`
                : `/waveform/${encodeURIComponent(trackId)}`;
            const { data } = await fetchContext.authAxios.get(endpoint, config);

            const buf = await parseAndPersistWaveform(data, trackId, denoised);
            if (buf) {
                return DecodePeaksDatToFloat32(buf);
            }
        } catch (error: any) {
            if (error?.response?.status === 401) {
                // This helper refreshes the token and replays the original request; use its response.
                const resp = await RefreshTokenAndRetry(error, authContext, fetchContext);

                const buf = await parseAndPersistWaveform(resp?.data, trackId, denoised);
                if (buf) {
                   return DecodePeaksDatToFloat32(buf);
                }
            }
        }
        return new Float32Array();
    }

	    const CreateSequence = async ({projectId, title} : { projectId: string, title: string }) => {
	        const userId = authContext.getUserId();
            const workspaceId = authContext.getCurrentWorkspaceId?.() || authContext.getDefaultWorkspaceId?.() || userId;
	        const seqRef = doc(collection(db, kSequencesCollectionName)); // auto-id
	        const now = nowUnixSec();
	        await setDoc(seqRef, {
	            sequenceId: seqRef.id,
	            title: title,
	            userId: userId,
                workspaceId,
	            projectId: projectId,
	            deleted: false,
	            createdAt: now,
	            updatedAt: now,
	        });
	        trackSessionEvent("sequence_created");
	        return seqRef.id;
	    }

	    const CreateComposition = async ({projectId} : { projectId: string }) => {
	        const userId = authContext.getUserId();
            const workspaceId = authContext.getCurrentWorkspaceId?.() || authContext.getDefaultWorkspaceId?.() || userId;
	        const compRef = doc(collection(db, kCompositionsCollectionName)); // auto-id
	        const now = nowUnixSec();
	        await setDoc(compRef, {
	            compositionId: compRef.id,
	            title: "Untitled",
	            userId: userId,
                workspaceId,
	            schemaVersion: PERSISTED_SCHEMA_VERSION,
	            isEmpty: true,
	            projectId: projectId,
	            deleted: false,
	            canvasWidth: 1920,
	            canvasHeight: 1080,
	            createdAt: now,
	            updatedAt: now,
	        });
	        trackSessionEvent("composition_created");
	        return compRef.id;
	    }

    const CreateSpeaker = async ({projectId, name} : { projectId: string, name: string }) => {
        const userId = authContext.getUserId();
        const workspaceId = authContext.getCurrentWorkspaceId?.() || authContext.getDefaultWorkspaceId?.() || userId;
        const speakerRef = doc(collection(db, kSpeakersCollectionName));
        const now = nowUnixSec();
        await setDoc(speakerRef, {
            speakerId: speakerRef.id,
            name: name,
            userId: userId,
            workspaceId,
            projectId: projectId,
            createdAt: now,
            updatedAt: now,
        });
        return speakerRef.id;
    }

	    const CreateProject = async (title: string = "Untitled Project", isEmpty: boolean = true) => {
	        const userId = authContext.getUserId();
            const workspaceId = authContext.getCurrentWorkspaceId?.() || authContext.getDefaultWorkspaceId?.() || userId;
	        const projectRef = doc(collection(db, kProjectsCollectionName)); // auto-id
	        const now = nowUnixSec();
	        await setDoc(projectRef, {
	            projectId: projectRef.id,
	            name: title,
	            userId: userId,
                workspaceId,
	            deleted: false,
	            isEmpty: isEmpty,
	            durationSecs: 0,
	            createTime: now,
	            updateTime: now,
	            lastViewed: now,
	        });
	        trackSessionEvent("project_created");
	
	        // Create a default composition for the project
	        await CreateComposition({projectId: projectRef.id});

        return projectRef.id;
    }


    const DeleteSequence = async ({sequenceId}) => {
        try {
            const entityRef = doc(db, kSequencesCollectionName, sequenceId);
            await updateDoc(entityRef, {deleted: true});
        } catch (e) {
            console.error("Failed to delete sequence", e);
        }
    }

    const RenameProject = async ({projectId, newName}: {projectId: string, newName: string}) => {
        try {
            const renameValue = (newName || "").trim();
            if (!renameValue) return;
            const entityRef = doc(db, kProjectsCollectionName, projectId);
            await updateDoc(entityRef, { name: renameValue });
        } catch (e) {
            console.error("Failed to rename project", e);
        }
    }

    const GetTranscript = async ({
                                     transcriptId,
                                     projectId,
                                 }: {
        transcriptId: string;
        projectId: string;
    }): Promise<any | undefined> => {
        if (!transcriptId) return undefined;

        // 1) Try local (OPFS) first via LocalAssetStore (keyed by transcriptId)
        try {
            const buf = await LocalAssetStore.GetTranscript({ transcriptId });
            if (buf) {
                const text = new TextDecoder("utf-8").decode(buf);
                const transcript = JSON.parse(text);
                // Validate cached transcript has words array (skip stale cache entries)
                if (transcript?.words && Array.isArray(transcript.words) && transcript.words.length > 0) {
                    return transcript;
                }
            }
        } catch {
            // ignore and fall through to server
        }

        // 2) Fallback: fetch from server (uses transcriptId directly)
        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${authContext.getToken()}` },
            };

            const { data: urlResponse } = await fetchContext.authAxios.get(
                `/transcript/${encodeURIComponent(transcriptId)}`,
                config
            );

            if (!urlResponse?.url) {
                return undefined;
            }

            const transcriptResponse = await fetch(urlResponse.url);
            if (!transcriptResponse.ok) {
                return undefined;
            }

            const data = await transcriptResponse.json();
            if (!data) return undefined;

            // Save to OPFS + Dexie for next time (keyed by transcriptId)
            try {
                await LocalAssetStore.SaveTranscript({
                    transcriptId,
                    projectId,
                    transcriptJson: data,
                });
            } catch {
                // Ignore caching errors
            }

            return data;
        } catch (error: any) {
            if (error?.response?.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
            return undefined;
        }
    };

    /**
     * Progressive transcription: fetch the manifest.json (via a signed URL).
     * Returns the manifest JSON, or undefined if not available yet / not chunked.
     */
    const GetTranscriptManifest = async ({
                                             transcriptId,
                                         }: {
        transcriptId: string;
    }): Promise<any | undefined> => {
        if (!transcriptId) return undefined;

        const config: AxiosRequestConfig = {
            headers: { Authorization: `Bearer ${authContext.getToken()}` },
            validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
        };

        try {
            const { data: urlResponse, status } = await fetchContext.authAxios.get(
                `/transcript/${encodeURIComponent(transcriptId)}?format=manifest`,
                config
            );

            if (status === 404) return undefined;
            if (!urlResponse?.url) return undefined;

            const resp = await fetch(urlResponse.url);
            if (!resp.ok) return undefined;
            return await resp.json();
        } catch (error: any) {
            if (error?.response?.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
            return undefined;
        }
    };

    /**
     * Progressive transcription: fetch a published chunk JSON (via a signed URL).
     * Returns the chunk JSON, or undefined if not available yet.
     */
    const GetTranscriptChunk = async ({
                                         transcriptId,
                                         chunkId,
                                     }: {
        transcriptId: string;
        chunkId: number;
    }): Promise<any | undefined> => {
        if (!transcriptId || !Number.isFinite(chunkId)) return undefined;

        const config: AxiosRequestConfig = {
            headers: { Authorization: `Bearer ${authContext.getToken()}` },
            validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
        };

        try {
            const { data: urlResponse, status } = await fetchContext.authAxios.get(
                `/transcript/${encodeURIComponent(transcriptId)}/chunks/${encodeURIComponent(String(chunkId))}`,
                config
            );

            if (status === 404) return undefined;
            if (!urlResponse?.url) return undefined;

            const resp = await fetch(urlResponse.url);
            if (!resp.ok) return undefined;
            return await resp.json();
        } catch (error: any) {
            if (error?.response?.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
            return undefined;
        }
    };

    /**
     * AI: Generate chapter markers (titles + anchor blocks) from an edited transcript.
     * Server is expected to run an LLM and return chapter starts as blockIds.
     */
    const PostAiChapters = async ({
        projectId,
        desiredCount,
        blocks,
        minSpacingSec = 30,
        maxChapters = 30,
    }: {
        projectId: string;
        desiredCount: number | null;
        blocks: Array<{ id: string; startSec: number; text: string }>;
        minSpacingSec?: number;
        maxChapters?: number;
    }): Promise<{ chapters: Array<{ blockId: string; title: string }> } | undefined> => {
        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${authContext.getToken()}` },
            };

            const { data } = await fetchContext.authAxios.post(
                `/ai/chapters`,
                {
                    projectId,
                    desiredCount,
                    minSpacingSec,
                    maxChapters,
                    blocks,
                },
                config,
            );

            const chapters = Array.isArray((data as any)?.chapters) ? (data as any).chapters : [];
            return { chapters };
        } catch (error: any) {
            if (error?.response?.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
            console.error("Failed to generate AI chapters:", error);
            return undefined;
        }
    };

    /**
     * AI: Detect retakes from edited transcript candidates.
     * Server is expected to run an LLM and return per-candidate decisions.
     */
    const PostAiRetakes = async ({
        projectId,
        policy,
        candidates,
    }: {
        projectId: string;
        policy?: {
            mode?: "conservative";
            minConfidence?: number;
            maxGapSec?: number;
            requireSameSource?: boolean;
            requireSameSpeakerWhenAvailable?: boolean;
        };
        candidates: Array<{
            id: string;
            sourceIndex: number;
            speakerKey: string;
            gapSec: number;
            lexicalSimilarity: number;
            heuristicScore: number;
            restartCueNearby: boolean;
            first: { segmentId: string; startSec: number; endSec: number; text: string };
            second: { segmentId: string; startSec: number; endSec: number; text: string };
            bridgeCueText?: string;
            contextBefore?: string;
            contextAfter?: string;
        }>;
    }): Promise<
        | {
              decisions?: Array<{
                  candidateId?: string;
                  decision?: "retake" | "not_retake";
                  remove?: "first" | "second";
                  confidence?: number;
                  reason?: string;
              }>;
              retakes?: Array<{
                  candidateId?: string;
                  remove?: "first" | "second";
                  confidence?: number;
                  reason?: string;
              }>;
          }
        | undefined
    > => {
        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${authContext.getToken()}` },
            };

            const { data } = await fetchContext.authAxios.post(
                `/ai/retakes`,
                {
                    projectId,
                    policy,
                    candidates,
                },
                config,
            );

            return {
                decisions: Array.isArray((data as any)?.decisions) ? (data as any).decisions : undefined,
                retakes: Array.isArray((data as any)?.retakes) ? (data as any).retakes : undefined,
            };
        } catch (error: any) {
            if (error?.response?.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
            console.error("Failed to detect AI retakes:", error);
            return undefined;
        }
    };

    /**
     * Focus analysis: fetch a published focus chunk JSON (via a signed URL).
     * Returns the chunk JSON, or undefined if not available yet.
     *
     * Server: GET /focus/{trackId}/chunks/{chunkIndex} -> { url }
     * Then fetch(url) -> { trackId, t0Sec, t1Sec, keyframes:[{ tSec, rect:{x,y,w,h}, confidence }] }
     */
    const GetFocusChunk = async ({
        trackId,
        chunkId,
    }: {
        trackId: string;
        chunkId: number;
    }): Promise<any | undefined> => {
        if (!trackId || !Number.isFinite(chunkId)) return undefined;

        const config: AxiosRequestConfig = {
            headers: { Authorization: `Bearer ${authContext.getToken()}` },
            validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
        };

        try {
            const { data: urlResponse, status } = await fetchContext.authAxios.get(
                `/focus/${encodeURIComponent(trackId)}/chunks/${encodeURIComponent(String(chunkId))}`,
                config
            );

            if (status === 404) return undefined;
            if (!urlResponse?.url) return undefined;

            const resp = await fetch(urlResponse.url);
            if (!resp.ok) return undefined;
            return await resp.json();
        } catch (error: any) {
            if (error?.response?.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
            return undefined;
        }
    };

    /**
     * Focus analysis: publish client-origin chunk0 for immediate UI framing.
     * Server: POST /focus/{trackId}/chunk0 -> publishes chunk0 + updates Firestore analysis doc.
     */
    const PostFocusChunk0 = async ({
        trackId,
        totalSec,
        frames,
    }: {
        trackId: string;
        totalSec: number;
        frames: Array<{ tSec: number; imageBase64: string }>;
    }): Promise<boolean> => {
        if (!trackId) return false;

        const config: AxiosRequestConfig = {
            headers: { Authorization: `Bearer ${authContext.getToken()}` },
        };

        try {
            await fetchContext.authAxios.post(
                `/focus/${encodeURIComponent(trackId)}/chunk0`,
                { totalSec: Number(totalSec ?? 0) || 0, frames: Array.isArray(frames) ? frames : [] },
                config
            );
            return true;
        } catch (error: any) {
            if (error?.response?.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
            return false;
        }
    };

    // Folder operations
    const CreateFolder = async ({projectId, name = "Untitled Folder"} : { projectId: string, name?: string }) => {
        const userId = authContext.getUserId();
        const workspaceId = authContext.getCurrentWorkspaceId?.() || authContext.getDefaultWorkspaceId?.() || userId;
        const folderRef = doc(collection(db, kFoldersCollectionName)); // auto-id
        const now = nowUnixSec();
        await setDoc(folderRef, {
            folderId: folderRef.id,
            name: name,
            userId: userId,
            workspaceId,
            projectId: projectId,
            deleted: false,
            fileIds: [], // Array of trackIds/assetIds
            createdAt: now,
            updatedAt: now,
        });
        return folderRef.id;
    }

    const RenameFolder = async ({folderId, newName}: {folderId: string, newName: string}) => {
        try {
            const renameValue = (newName || "").trim();
            if (!renameValue) return;
            const folderRef = doc(db, kFoldersCollectionName, folderId);
            await updateDoc(folderRef, { name: renameValue, updatedAt: nowUnixSec() });
        } catch (e) {
            console.error("Failed to rename folder", e);
        }
    }

    const DeleteFolder = async ({folderId}: {folderId: string}) => {
        try {
            const folderRef = doc(db, kFoldersCollectionName, folderId);
            await updateDoc(folderRef, { deleted: true, updatedAt: nowUnixSec() });
        } catch (e) {
            console.error("Failed to delete folder", e);
        }
    }

    const AddFileToFolder = async ({folderId, fileId}: {folderId: string, fileId: string}) => {
        try {
            const folderRef = doc(db, kFoldersCollectionName, folderId);
            await updateDoc(folderRef, { 
                fileIds: arrayUnion(fileId),
                updatedAt: nowUnixSec() 
            });
        } catch (e) {
            console.error("Failed to add file to folder", e);
        }
    }

    const RemoveFileFromFolder = async ({folderId, fileId}: {folderId: string, fileId: string}) => {
        try {
            const folderRef = doc(db, kFoldersCollectionName, folderId);
            await updateDoc(folderRef, { 
                fileIds: arrayRemove(fileId),
                updatedAt: nowUnixSec() 
            });
        } catch (e) {
            console.error("Failed to remove file from folder", e);
        }
    }

    const MoveFileToFolder = async ({fileId, fromFolderId, toFolderId}: {fileId: string, fromFolderId?: string, toFolderId?: string}) => {
        try {
            // Remove from old folder if specified
            if (fromFolderId) {
                await RemoveFileFromFolder({folderId: fromFolderId, fileId});
            }
            // Add to new folder if specified (toFolderId = undefined means "move to root/unfiled")
            if (toFolderId) {
                await AddFileToFolder({folderId: toFolderId, fileId});
            }
        } catch (e) {
            console.error("Failed to move file to folder", e);
        }
    }

    const RetranscribeTrack = async ({
        trackId,
        language,
        numSpeakers,
        skipDenoising,
    }: {
        trackId: string;
        language?: string;
        numSpeakers?: number | 'auto';
        skipDenoising?: boolean;
    }): Promise<boolean> => {
        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${authContext.getToken()}` },
            };

            const body: { language?: string; numSpeakers?: number; skipDenoising?: boolean } = {};
            if (language && language !== 'auto') {
                body.language = language;
            }
            if (numSpeakers && numSpeakers !== 'auto') {
                body.numSpeakers = numSpeakers;
            }
            if (skipDenoising) {
                body.skipDenoising = true;
            }

            await fetchContext.authAxios.post(
                `/transcribe/${encodeURIComponent(trackId)}`,
                body,
                config
            );

            return true;
        } catch (error: any) {
            if (error?.response?.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
            console.error('Failed to re-transcribe track:', error);
            return false;
        }
    };

    /**
     * Get signed URL for enhanced (Studio Sound via Auphonic) audio.
     * Returns undefined if enhanced audio is not ready.
     */
    const GetEnhancedAudioUrl = async ({trackId}: { trackId: string }): Promise<string | undefined> => {
        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${authContext.getToken()}` },
            };
            const { data } = await fetchContext.authAxios.get(
                `/track/${encodeURIComponent(trackId)}/enhanced`,
                config
            );
            return data?.url as (string | undefined);
        } catch (error: any) {
            if (error?.response?.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
            // 404 means enhanced audio not ready - return undefined
            return undefined;
        }
    };

    /**
     * Get waveform data for enhanced (Studio Sound via Auphonic) audio.
     * Falls back to server endpoint for the enhanced waveform.
     */
    const GetEnhancedWaveformData = async ({trackId}: { trackId: string }): Promise<Float32Array> => {
        // Try local first (with enhanced=true flag)
        try {
            const local = await LocalAssetStore.GetAudioWaveformData({ trackId, denoised: true });
            if (local) {
                let peaksF32: Float32Array | null = null;
                if (local instanceof Float32Array) {
                    peaksF32 = local;
                } else if (local instanceof ArrayBuffer) {
                    peaksF32 = DecodePeaksDatToFloat32(local);
                }
                if (peaksF32 && peaksF32.length > 0) return peaksF32;
            }
        } catch (e) {
            console.log("Enhanced waveform not found locally:", e.message);
        }

        // Server fallback: GET /waveform/:trackId/enhanced
        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${authContext.getToken()}` },
            };
            const { data } = await fetchContext.authAxios.get(
                `/waveform/${encodeURIComponent(trackId)}/enhanced`,
                config
            );
            const buf = await parseAndPersistWaveform(data, trackId, true);
            if (buf) {
                return DecodePeaksDatToFloat32(buf);
            }
        } catch (error: any) {
            if (error?.response?.status === 401) {
                const resp = await RefreshTokenAndRetry(error, authContext, fetchContext);
                const buf = await parseAndPersistWaveform(resp?.data, trackId, true);
                if (buf) {
                    return DecodePeaksDatToFloat32(buf);
                }
            }
        }
        return new Float32Array();
    };

    /**
     * Trigger professional enhancement (Studio Sound via Auphonic) for a track.
     * Used to manually trigger or retry enhancement.
     */
    const TriggerEnhance = async ({trackId}: { trackId: string }): Promise<boolean> => {
        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${authContext.getToken()}` },
            };
            await fetchContext.authAxios.post(
                `/track/${encodeURIComponent(trackId)}/enhance`,
                {},
                config
            );
            return true;
        } catch (error: any) {
            if (error?.response?.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
            console.error('Failed to trigger enhancement:', error);
            return false;
        }
    };

    /**
     * Promote a backup (LiveKit SFU) track to replace a stuck primary track.
     * Used when a local recording is stuck in "recording" state and backup is available.
     */
    const PromoteBackupTrack = async ({backupTrackId}: { backupTrackId: string }): Promise<boolean> => {
        try {
            const config: AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${authContext.getToken()}` },
            };
            await fetchContext.authAxios.post(
                `/tracks/promote-backup`,
                { backupTrackId },
                config
            );
            return true;
        } catch (error: any) {
            if (error?.response?.status === 401) {
                await RefreshTokenAndRetry(error, authContext, fetchContext);
            }
            console.error('Failed to promote backup track:', error);
            return false;
        }
    };

    return {UploadAsset, UploadNonLocalFirstAsset, DeleteProject, BringLegacyProjectToNewLibretto, DeleteTrack, DeleteAsset, GetThumbnailUrl, UndeleteProject, RenameComposition, DuplicateComposition, RenameSequence, ResolveAssetUrl, CreateSpeaker, DeleteComposition, CreateComposition, CreateProject, RenameTrackOrAsset, GetTranscript, GetTranscriptManifest, GetTranscriptChunk, PostAiChapters, PostAiRetakes, GetFocusChunk, PostFocusChunk0, GetTrackUrl, GetAssetUrl, GetWaveformData, GetEnhancedAudioUrl, GetEnhancedWaveformData, TriggerEnhance, CreateSequence, DeleteSequence, RenameProject, CreateFolder, RenameFolder, DeleteFolder, AddFileToFolder, RemoveFileFromFolder, MoveFileToFolder, RetranscribeTrack, PromoteBackupTrack, SetActiveTranscriptionIdForTrack};
}
